import { BREADCRUMBS } from 'breadcrumbs/constants/breadcrumbs';
import qs from 'qs';
import { getBreadcrumbs } from 'server/utils/breadcrumbs/getBreadcrumbs';
import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { IProductLandingPage } from 'shared/types/ProductLandingPage';
import { PlpPageComponent as PLPLandingPageComponent } from 'product-landing-pages/components/PlpPageComponent';
import { getBrowseResults } from 'server/utils/constructor/getBrowseResults';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { t } from 'localization/utils/t';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { FeatureFlags } from 'featureFlags/constants';
import { getConsumerSearchServerSideData } from 'server/utils/getConsumerSearchDataServerSide';

const TP = 'pages.instant';

interface IInstantShip extends IProductLandingPage {
  metaDescription?: string;
  consumerSearchFilters?: Record<string, any>;
  consumerSearchInitialData?: string;
}

const InstantShip = ({
  breadcrumbs,
  browseKey,
  browseValue,
  constructorResponse,
  displayName,
  globalPricingId,
  isCrawler = false,
  consumerSearchFilters,
  consumerSearchInitialData = '{}',
}: IInstantShip) => {
  const {
    name = displayName,
    description = '',
    metadataDescription,
    titleTag = '',
  } = breadcrumbs || {};

  return (
    <PLPLandingPageComponent
      browseKey={browseKey}
      browseValue={browseValue}
      constructorResponse={constructorResponse}
      description={description}
      displayName={name}
      globalPricingId={globalPricingId}
      metadataDescription={
        metadataDescription ||
        t(
          `${TP}.defaultMetaDescription`,
          'Shop Instant now on GOAT. Instant items are pre-verified, ready to ship directly from GOAT and receive priority processing in our facilities.',
        )
      }
      metaTitle={titleTag || t(`${TP}.metaTitle`, 'Instant ')}
      isCrawler={isCrawler}
      consumerSearchFilters={consumerSearchFilters}
      consumerSearchInitialData={JSON.parse(consumerSearchInitialData)}
    />
  );
};

export const getServerSideProps = async ({ query, resolvedUrl, req, res, locale }) => {
  const q = qs.parse(query);
  const globalPricingId = await fetchGlobalPricingId({ req, res });
  const isCrawler = isSearchCrawler(req?.headers);
  const isConsumerSearchEnable =
    req.featureFlags?.[FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND];

  const breadcrumbs = await getBreadcrumbs({
    req,
    res,
    path: `${BREADCRUMBS.INSTANT}`,
  });

  const consumerSearchFilters = {
    instantShip: 'true',
  };

  if (isConsumerSearchEnable) {
    // If consumer search is enabled, we need to fetch the consumer data details
    const consumerSearchInitialData = await getConsumerSearchServerSideData({
      filters: { ...q, pageFilters: consumerSearchFilters },
      pageNumber: req.query?.pageNumber || 1,
    });

    if (!consumerSearchInitialData?.items?.hasData) {
      return { notFound: true };
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        breadcrumbs,
        browseKey: 'group_id',
        browseValue: 'all',
        displayName: 'Instant',
        globalPricingId,
        isCrawler,
        consumerSearchFilters,
        consumerSearchInitialData: JSON.stringify({}),
      },
    };
  }

  try {
    const browseResponse = await getBrowseResults({
      req,
      res,
      query,
      resolvedUrl,
      filterKey: 'group_id',
      filterValue: 'all',
    });

    const invalidSearch = browseResponse.response.total_num_results === 0;

    if (invalidSearch) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        breadcrumbs,
        browseKey: 'group_id',
        browseValue: 'all',
        constructorResponse: browseResponse,
        displayName: 'Instant',
        globalPricingId,
        isCrawler,
        consumerSearchFilters,
      },
    };
  } catch {
    return { notFound: true };
  }
};

export default InstantShip;
